import { useCallback, useEffect, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, Typography } from "@mui/material";
import {
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import CollapsibleCard from "./CollapsibleCard";
import { apiGetCreLinks, targetTypeForRefCollection } from "../../graphQL/creLinks";
import { apiGetFiles, apiGetFileSignedUrl } from "../../graphQL/file";
import { getSettingsDomain } from "../../graphQL/settings/scope";
import { myToast } from "../../libs";

// One implementation for every entity detail view (property, contact, deal):
// the documents attached to this entity through the CRE link graph, with the
// link's role and origin. Read-only.
const LinkedDocumentsCard = ({ refCollection, refId, defaultExpanded = false }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    const targetType = targetTypeForRefCollection(refCollection);
    if (!targetType || !refId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const links = (await apiGetCreLinks({ targetType, targetId: refId, sourceType: "file" }))
        .filter((l) => l.sourceType === "file");
      if (links.length === 0) {
        setRows([]);
        return;
      }
      const { files } = await apiGetFiles({
        domain: getSettingsDomain(),
        ids: links.map((l) => l.sourceId),
        pagination: { page: 1, limit: 100 },
      });
      const byId = new Map((files || []).map((f) => [f.id, f]));
      setRows(
        links
          .map((link) => ({ link, file: byId.get(link.sourceId) }))
          .filter((r) => r.file && r.file.isCurrent !== false),
      );
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [refCollection, refId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (file) => {
    try {
      const { signedUrl } = await apiGetFileSignedUrl(file.id, file.authorId);
      if (signedUrl) window.open(signedUrl, "_blank", "noopener");
    } catch (err) {
      myToast.error("Could not get a download link");
    }
  };

  const displayName = (file) =>
    (file.description && file.description.trim()) ||
    (file.key ? file.key.split("/").pop() : file.id);

  return (
    <CollapsibleCard
      title="Linked documents"
      summary={rows.length ? `${rows.length} document${rows.length === 1 ? "" : "s"}` : "None"}
      defaultExpanded={defaultExpanded}
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
          <CircularProgress size={16} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ px: 1.25, py: 1, fontSize: 11.5, color: "#94A3B8" }}>
          No documents linked yet.
        </Typography>
      ) : (
        rows.map(({ link, file }) => (
          <Box
            key={link.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderBottom: "1px solid #F4F5FB",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <DescriptionIcon sx={{ fontSize: 16, color: "#3949AB" }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#1A1F36",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName(file)}
              </Typography>
              <Typography sx={{ fontSize: 10, color: "#94A3B8" }}>
                {[
                  file.docType && file.docType !== "other" ? file.docType.toUpperCase() : "",
                  Number(file.version) > 1 ? `v${file.version}` : "",
                ]
                  .filter(Boolean)
                  .join(" • ") || file.type || "File"}
              </Typography>
            </Box>
            <Chip
              label={link.role}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                backgroundColor: link.origin === "user" ? "#E8EAF6" : "#F4F5FB",
                color: "#3949AB",
              }}
            />
            <IconButton size="small" onClick={() => handleDownload(file)} sx={{ width: 24, height: 24, color: "#64748B" }}>
              <DownloadIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))
      )}
    </CollapsibleCard>
  );
};

export default LinkedDocumentsCard;
